<script>
    import AssessmentTypeModel, {getSortedValues} from '../../Models/assessmentTypeModel';
    import AssessmentTypeNav from '../AssessmentTypeNav.svelte';
    import Loader from '../Loader.svelte';
    import {addFormModal} from '../../Forms/ModalFormManager.svelte';
    import {confirm} from '../Confirmation.svelte';
    import { showContextMenu } from '../ContextMenu.svelte';

    import PolicySection from './Sections/PolicySection.svelte';
    import AssessmentTypeScores from './Sections/AssessmentTypeScores.svelte';

    import Dashboard from '../../Models/dashboardModel';
    import Library from '../../Models/libraryORM';
    import { onMount } from 'svelte';
    import GlobalCSSEditor from './GlobalCSSEditor.svelte';
    import ExcelVisualEditor from './ExcelVisualEditor.svelte';
    import SlideEditor from '../Slide Editor/SlideEditor.svelte';
    import {clean} from '../../Utilities/Cleaner.svelte';

    import FormSection from './AssessmentType/FormSection.svelte';
    import DashboardSection from './AssessmentType/DashboardSection.svelte';
    import LibrarySection from './AssessmentType/LibrarySection.svelte';

    import sortable, {getBeforeAfter} from './../../lib/sortable';


    let dashboards = Dashboard.getDashboardList();
    let libraries = Library.getList();

    export let params;

    let assessment_type_model = new AssessmentTypeModel(params.id);
    let assessment_type = assessment_type_model.store;

    let selected_section;

    let section_form = [
        {
            name: "section_name",
            label: "Name",
            required: true
        },
        {
            name: "section_description",
            label: "Description",
            type: "tinymce",
            required: true
        },
        {
            name: "section_type",
            label: "Section Type",
            type: "select",
            dflt: "form",
            choices: {
                static: [
                    {label: "Form", value:"form"},
                    {label: "Findings", value: "findings"},
                    {label: "Library", value:"library"},
                    {label: "Dashboard", value: "dashboard"},
                    {label: "Policies", value: "policies"},
                    {label: "Scores", value: "scores"}
                ]
            }
        },
        {
            name: "dashboard_type",
            label: "Dashboard Type",
            type: "select",
            required: true,
            default:"",
            choices: {
                static: []
            },
            preq: {
                field: "section_type",
                value: "dashboard"
            }
        },
        {
            name: "library_type",
            label: "Library Type",
            type: "select",
            required: true,
            default:"",
            choices: {
                static: []
            },
            preq: {
                field: "section_type",
                value: "library"
            }
        }
    ]

    let section_edit_form = [
        {
            name: "section_name",
            label: "Name",
            required: true
        },
        {
            name: "section_description",
            label: "Description",
            type: "tinymce",
            required: true
        }
    ]

    function addSection() {
        addFormModal({
            title: "Add Section",
            fields: section_form
        }).then ((section) => {
            assessment_type_model.addSection(section);

        }).catch(console.info)
    }

    function handleContextMenu(evt, section) {
        showContextMenu(evt.clientX, evt.clientY, [
            {
                name: "Edit Section Name",
                action: () => editSection(section)
            },
            {
                name: "Delete Section",
                action: () => deleteSection(section)
            }
        ])
    }

    function editSection(section) {
        addFormModal({
            title: "Edit Section",
            fields: section_edit_form,
            data: section
        }).then((data) => {
            assessment_type_model.saveSection(data)
        });
    }

    function deleteSection(section) {
        confirm(`Are you sure you want to delete ${section.section_name}?`).then(() => {
            if (selected_section?.section_id == section.section_id) {
                selected_section = undefined;
            }
            assessment_type_model.deleteSection(section);
        }).catch(console.info);
    }

    function selectSection(section_id, assessment_type) {
        selected_section = undefined;

        if (!assessment_type) {
            return;
        }

        if (section_id == "css") {
            return;
        }

        for (let section of assessment_type.report_sections) {
            if (section.section_id == section_id) {
                selected_section = section;
                return
            }
        }
    }

    function moveSection(evt) {
        if (evt.newIndex == evt.oldIndex) return;

        let sections = getSortedValues($assessment_type.report_sections);

        let {before, after} = getBeforeAfter(sections, evt.oldIndex, evt.newIndex);

        assessment_type_model.moveSection(sections[evt.oldIndex], after, before);
    }

    $: selectSection(params.section_id, $assessment_type);

    onMount(() => {
        let dash_unsub = dashboards.subscribe((dashboards) => {
            if (!dashboards || dashboards.length == 0) return;

            section_form[3].dflt = dashboards[0]._id;
            section_form[3].choices = {
                static: dashboards.map((dash) => {return {label: dash.name, value: dash._id}})
            }
        });

        let lib_unsub = libraries.subscribe((libraries) => {
            if (!libraries || libraries.length == 0) return;

            section_form[4].dflt = libraries[0]._id
            section_form[4].choices = {
                static: libraries.map((lib) => {return {label: lib.name, value: lib._id}})
            }
        })

        return () => {
            dash_unsub();
            lib_unsub();
        }
    })
</script>

<Loader loading={!$assessment_type}>
<div class="container viewport is-fluid">
    <div class="columns viewport">
        <div class="column is-2">
            <AssessmentTypeNav assessment_type_id={params.id}></AssessmentTypeNav>
            <h1 class="title is-1">{$assessment_type.details.name}</h1>

            <aside class="menu">
                <p class="menu-label">Report Sections</p>
                <ul class="menu-list" use:sortable on:sortEnd={ (evt) => moveSection(evt.detail) }>
                    {#each getSortedValues($assessment_type.report_sections) as section (section.section_id)}
                        <li on:contextmenu|preventDefault="{evt => handleContextMenu(evt, section)}">
                            <a class="link" class:is-active={selected_section && selected_section.section_id == section.section_id} href="#/assessmentTypes/{params.id}/Report/{section.section_id}/">
                                {section.section_name}
                            </a>
                        </li>
                    {/each}
                    <li>
                        <span class="link" on:click={addSection}> + Add Section</span>
                    </li>
                </ul>
                <p class="menu-label">Report Output</p>
                <ul class="menu-list">
                    <li><a class:is-active={params.section_id == "ppt"} class="link" href="#/assessmentTypes/{params.id}/Report/ppt/">Customize PPT</a></li>
                    <li><a class:is-active={params.section_id == "css"} class="link" href="#/assessmentTypes/{params.id}/Report/css/">Customize Global CSS</a></li>
                    <li><a class:is-active={params.section_id == "excel"} class="link" href="#/assessmentTypes/{params.id}/Report/excel/">Customize Excel Template</a></li>
                </ul>
                <!--
                    <span class="button is-fullwidth is-outlined is-info" on:click={addSection}>Add Section</span>
                -->
            </aside>
        </div>
        <div class="column">
            {#key selected_section}
                {#if selected_section}
                    <h1 class="title is-1">{selected_section.section_name}</h1>
                    {#if selected_section.section_description}
                        <div class="description">
                            {@html clean(selected_section.section_description)}
                        </div>
                    {/if}
                    {#if selected_section.section_type == "form"}
                        <FormSection section={selected_section} {assessment_type_model}></FormSection>
                    {:else if selected_section.section_type == "dashboard"}
                        <DashboardSection section={selected_section} {assessment_type_model} {assessment_type}></DashboardSection>
                    {:else if selected_section.section_type == "library"}
                        <LibrarySection section={selected_section} {assessment_type_model}></LibrarySection>
                    {:else if selected_section.section_type == "policies"}
                        <PolicySection {assessment_type_model} section={selected_section} />
                    {:else if selected_section.section_type == "scores" }
                        <AssessmentTypeScores {assessment_type_model} section={selected_section} />
                    {:else}
                        <h3 class="title is-3">This section will be populated with data from the Assessment</h3>
                    {/if}
                {:else if params.section_id == 'ppt'}
                    <SlideEditor {assessment_type} {assessment_type_model}></SlideEditor>
                {:else if params.section_id == "css"}
                    <GlobalCSSEditor {assessment_type} {assessment_type_model}></GlobalCSSEditor>
                {:else if params.section_id == "excel"}
                    <ExcelVisualEditor {assessment_type_model} {assessment_type} />
                {:else}
                    <h1 class="title is-1">Select a section on the left.</h1>
                {/if}
            {/key}
        </div>
    </div>
</div>
</Loader>


<style>
    .description {
        padding: 0 32px;
    }
</style>
